import Button from "@/common/components/Button";
import { ImageWithPlaceholder } from "@/common/components/ImageWithPlaceholder";
import { unities } from "@/common/constants/unities.constants";
import useUserPermissions from "@/common/hooks/useUserPermissions";
import { formatCurrency } from "@/common/utils/formatters";
import { EstablishmentCompanyData } from "@/features/company/types/company.type";
import { useEstablishmentMap } from "@/features/establishments/stores/useEstablishmentMap.store";
import { FuelIcon, HandshakeIcon } from "lucide-react";
import React from "react";
import { Link } from "react-router-dom";
import { Tooltip } from "react-tooltip";
import { twMerge } from "tailwind-merge";
import { animatedPanTo } from "../../utils/animatedPanTo";
import { formatDateTimePriceUpdatedAt } from "../../utils/formatters";
import { smoothZoomIn } from "../../utils/smoothZoom";

export const EstablishmentTooltipDetails = ({
  establishment,
  companyKind,
  cardList,
  setFilterBounds,
}: {
  establishment?: EstablishmentCompanyData;
  companyKind?: string;
  cardList?: boolean;
  setFilterBounds?: (bounds: { top_left_lat: number; top_left_lng: number; bottom_right_lat: number; bottom_right_lng: number; }) => void;
}) => {
  const {
    map,
    colorScheme,
    hoveredMarkerId,
    updateHoveredMarkerId,
    updateHoveredOnList,
    updateBoundsChangedOnCardClick,
    updateHoveredEstablishmentData,
    clusteredEstablishmentsIds,
    updateIsAnimating,
  } = useEstablishmentMap();

  const { hasUserPermission } = useUserPermissions();

  const establishmentAddress = establishment?.general_information?.address;
  const hasEstablishmentCoordinates = establishmentAddress?.latitude && establishmentAddress?.longitude;
  const establishmentCoordinates = { lat: Number(establishmentAddress?.latitude), lng: Number(establishmentAddress?.longitude) };

  const handleMouseLeave = () => {
    updateHoveredMarkerId('');
    updateHoveredEstablishmentData(undefined);
  };

  const handleMouseEnter = () => {
    if (cardList) {
      updateHoveredOnList(true);
      updateHoveredMarkerId(establishment?.id);
      updateHoveredEstablishmentData(establishment);
    }
  };

  const handleClick = async () => {
    if (cardList && hasEstablishmentCoordinates) {
      updateBoundsChangedOnCardClick(true);
      updateHoveredOnList(true);

      if (!clusteredEstablishmentsIds?.includes(establishment?.id || '') && map) {
        await animatedPanTo({
          map,
          newCenter: establishmentCoordinates,
          onStartAnimation: () => updateIsAnimating(true),
          onEndAnimation: () => updateIsAnimating(false),
          onSetFinalPosition: (finalBounds) => setFilterBounds?.({
            top_left_lat: finalBounds.topLeft.lat,
            top_left_lng: finalBounds.topLeft.lng,
            bottom_right_lat: finalBounds.bottomRight.lat,
            bottom_right_lng: finalBounds.bottomRight.lng,
          }),
        });
      } else {
        map?.panTo(new google.maps.LatLng(establishmentCoordinates.lat, establishmentCoordinates.lng));

        const currentZoom = map?.getZoom();

        if (map && currentZoom && currentZoom < 12) {
          await smoothZoomIn({
            map,
            initialZoom: currentZoom,
            finalZoom: 12,
          });
        }
      }

      updateHoveredMarkerId(establishment?.id);
      updateHoveredEstablishmentData(establishment);
    }
  };

  return (
    <>
      {establishment?.matrix_branch?.photos[0]?.url && !cardList && (
        <div className="w-full h-[128px] rounded-t-md">
          <img
            src={establishment.matrix_branch.photos[0].url}
            className="object-fill w-full h-full rounded-t-md"
          />
        </div>
      )}

      <div
        className={twMerge(
          'w-[336px] flex flex-col cursor-pointer transition-colors',
          hoveredMarkerId === establishment?.id && cardList && 'bg-sky-600/20 transition rounded-md establishment-card-hovered',
        )}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}
      >
        <div
          className={twMerge(
            'flex flex-col p-3 bg-slate-50/10',
            !establishment?.matrix_branch?.photos[0]?.url && 'rounded-t-md',
            (!Number(establishment?.product_prices?.length)) && 'rounded-b-md',
          )}
        >
          <div className="flex items-center gap-2 justify-between">
            <div className="flex gap-2 items-center">
              <ImageWithPlaceholder
                url={establishment?.general_information?.company_logo?.url}
                placeholderIcon={FuelIcon}
                className="h-6 w-6"
                iconClassName="h-4 w-4"
              />

              <p className={twMerge('font-bold text-lg line-clamp-3 text-ellipsis overflow-hidden', cardList && 'text-sm')}>{establishment?.general_information?.fantasy_name}</p>
            </div>

            {(companyKind === 'private_company' && hasUserPermission('Client::Negotiations::Create')) && (
              <Link to={`/negotiations/create?establishment_id=${establishment?.id}`}>
                <Button variant="default" className="text-xs p-2" onClick={(e) => e.stopPropagation()}>
                  Negociar

                  <HandshakeIcon size={16} />
                </Button>
              </Link>
            )}
          </div>

          <p className={twMerge('font-light mt-1 text-sm -skew-x-6', cardList && 'text-xs')}>
            {`${establishment?.general_information?.address.street},
            ${establishment?.general_information?.address.number} -
            ${establishment?.general_information?.address.city?.state?.acronym},
            ${establishment?.general_information?.address.city?.display_name}`}
          </p>
        </div>

        {(Number(establishment?.product_prices?.length) > 0) && (
          <div className={twMerge('flex-col p-3 rounded-b-md', colorScheme === 'dark' && cardList && 'bg-white-100')}>
            <div className="flex items-end">
              {establishment?.product_prices?.slice(0, 3).map((productPrice, index) => (
                <React.Fragment key={productPrice.id}>
                  {index > 0 && <div className="bg-gray-500 w-[1px] h-10 mr-2 self-center" />}

                  <div className="flex-col w-1/3" data-tooltip-id={productPrice.id}>
                    <p className="font-bold text-xs text-nowrap max-w-28 overflow-hidden text-ellipsis">
                      {productPrice.product.display_name}
                    </p>

                    <p className={twMerge('font-normal text-lg max-w-24 overflow-hidden text-ellipsis', cardList && 'text-sm')}>
                      {formatCurrency(Number(productPrice.value) || 0)}

                      <span className="font-light text-sm text-white-400">
                        {unities[productPrice.product.unit_for_quantity] ? `/` : ''}{unities[productPrice.product.unit_for_quantity]}
                      </span>
                    </p>

                    <p className={twMerge('font-thin italic -skew-x-6 text-white-300', cardList && 'text-xs')}>
                      {productPrice.updated_at && formatDateTimePriceUpdatedAt(new Date(productPrice.updated_at))}
                    </p>

                    {(`${formatCurrency(Number(productPrice.value) || 0)}/${unities[productPrice.product.unit_for_quantity]}`.length > 10) && (
                      <Tooltip id={productPrice.id}>
                        <div className="flex flex-col items-center gap-2">
                          <p className={twMerge('font-thin italic text-white-400', cardList && 'text-xs')}>
                            {productPrice.product.display_name}
                          </p>

                          <p className={twMerge('font-normal text-lg', cardList && 'text-sm')}>
                            {formatCurrency(Number(productPrice.value) || 0)}

                            <span className="font-light text-sm text-white-400">
                              {unities[productPrice.product.unit_for_quantity] ? `/` : ''}{unities[productPrice.product.unit_for_quantity]}
                            </span>
                          </p>
                        </div>
                      </Tooltip>
                    )}
                  </div>
                </React.Fragment>
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
};
